import { api, extractApiErrorMessage } from './api';

// Types
export interface User {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
  role: 'CUSTOMER' | 'YOGA_SCHOOL' | 'SUPPORT_ADMIN' | 'SUPER_ADMIN';
  isActive: boolean;
  emailVerified: boolean;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED';
  businessName?: string;
  businessType?: 'INDIVIDUAL' | 'ORGANIZATION';
  bio?: string;
  website?: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  verifiedUsers: number;
  customers: number;
  yogaSchools: number;
  supportAdmins: number;
  superAdmins: number;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  role?: string;
  isActive?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetUsersResponse {
  users: User[];
  pagination: PaginationInfo;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/** Booking counts for deletion preview (by status + program month on selectedMonth). */
export interface BookingDeletionBreakdown {
  total: number;
  pending: number;
  approved: number;
  cancelled: number;
  rejected: number;
  activeUpcomingProgramMonths: number;
  activeCurrentProgramMonth: number;
  activePastProgramMonths: number;
  activeUnknownProgramMonth: number;
  activeTotal: number;
}

export interface UserDeletionImpactData {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
  staffMembershipsRemoved: number;
  schoolsDeleted: number;
  schoolNames: string[];
  coursesOnOwnedSchools: number;
  bookingsOnOwnedSchools: number;
  /** Bookings placed at schools this user owns (customers of their school). */
  ownedSchoolBookingsBreakdown: BookingDeletionBreakdown;
  customerBookings: number;
  /** Bookings where this user is the customer (any school); same breakdown shape. */
  customerBookingsBreakdown: BookingDeletionBreakdown;
  courseReviews: number;
  messages: number;
  consultations: number;
  testimonials: number;
  usersWithCreatedByAdminRef: number;
  coursesWithCreatedByAdminRef: number;
  lastSuperAdminWarning: boolean;
}

// User Service Class
export class UserService {
  private static instance: UserService;

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  // Get users with pagination and filters
  async getUsers(params: GetUsersParams = {}): Promise<ApiResponse<GetUsersResponse>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.role) queryParams.append('role', params.role);
      if (params.isActive) queryParams.append('isActive', params.isActive);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`/users?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch users');
    }
  }

  // Get user by ID
  async getUserById(id: string): Promise<ApiResponse<User>> {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user');
    }
  }

  // Update user (admin). Use setYogaSchoolVerificationStatus for document verification only.
  async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating user:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user');
    }
  }

  /** Admin-only: yoga school owner document verification (backend enforces admin role). */
  async setYogaSchoolVerificationStatus(
    id: string,
    verificationStatus: NonNullable<User['verificationStatus']>
  ): Promise<ApiResponse<User>> {
    return this.updateUser(id, { verificationStatus });
  }

  async getUserDeletionImpact(id: string): Promise<ApiResponse<UserDeletionImpactData>> {
    try {
      const response = await api.get(`/users/${id}/deletion-impact`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user deletion impact:', error);
      throw new Error(extractApiErrorMessage(error, 'Failed to load deletion summary'));
    }
  }

  /** Permanent delete: send `{ confirmPhrase: 'DELETE' }` in the DELETE body (Super Admin only). */
  async permanentlyDeleteUser(id: string, confirmPhrase: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/users/${id}`, { data: { confirmPhrase } });
      return response.data;
    } catch (error: any) {
      console.error('Error permanently deleting user:', error);
      throw new Error(extractApiErrorMessage(error, 'Failed to delete user'));
    }
  }

  // Toggle user status (activate/deactivate)
  async toggleUserStatus(id: string, isActive: boolean): Promise<ApiResponse<User>> {
    try {
      const response = await api.put(`/users/${id}/toggle-status`, { isActive });
      return response.data;
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      throw new Error(error.response?.data?.message || 'Failed to toggle user status');
    }
  }

  // Update user role
  async updateUserRole(id: string, role: User['role']): Promise<ApiResponse<User>> {
    try {
      const response = await api.put(`/users/${id}/role`, { role });
      return response.data;
    } catch (error: any) {
      console.error('Error updating user role:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user role');
    }
  }

  // Get user statistics
  async getUserStats(): Promise<ApiResponse<UserStats>> {
    try {
      const response = await api.get('/users/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user statistics');
    }
  }

  // Search users with debouncing
  async searchUsers(searchTerm: string, params: Omit<GetUsersParams, 'search'> = {}): Promise<ApiResponse<GetUsersResponse>> {
    return this.getUsers({ ...params, search: searchTerm });
  }

  // Get users by role
  async getUsersByRole(role: User['role'], params: Omit<GetUsersParams, 'role'> = {}): Promise<ApiResponse<GetUsersResponse>> {
    return this.getUsers({ ...params, role });
  }

  // Get active/inactive users
  async getUsersByStatus(isActive: boolean, params: Omit<GetUsersParams, 'isActive'> = {}): Promise<ApiResponse<GetUsersResponse>> {
    return this.getUsers({ ...params, isActive: isActive.toString() });
  }
}

// Export singleton instance
export const userService = UserService.getInstance();

// Utility functions
export const formatUserRole = (role: User['role']): string => {
  const roleMap = {
    CUSTOMER: 'Customer',
    YOGA_SCHOOL: 'Yoga School',
    SUPPORT_ADMIN: 'Support Admin',
    SUPER_ADMIN: 'Super Admin'
  };
  return roleMap[role] || role;
};

export const formatVerificationStatus = (status: User['verificationStatus']): string => {
  const statusMap = {
    PENDING: 'Pending',
    VERIFIED: 'Verified',
    REJECTED: 'Rejected'
  } as const;
  return status ? statusMap[status] || 'Unknown' : 'Unknown';
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return formatDate(dateString);
};
